import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

// Confirm withdrawing a pending request — replaces the old window.confirm +
// post-hoc window.alert (2026-07-24). Shows the refund BEFORE the client commits, then the actual
// server figure afterwards. Inline-styled on purpose: the portal bundles every
// CSS file globally, so inline keeps this modal immune to selector bleed.

const money = (n) =>
    `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const REQUEST_NOUN = {
    create_booking: 'booking request',
    edit_booking: 'booking change request',
    cancel_booking: 'cancellation request',
    create_property: 'property request',
    edit_property: 'property change request',
    edit_client_profile: 'account change request',
    edit_client_tasks: 'tasks change request',
};

// Captured card money currently held for this request — the direct-rail receipt
// is credited back to the wallet IN FULL on withdrawal. Read from the SERVER's
// funding_legs; no arithmetic beyond the sum (money contract: server truth).
function capturedCardTotal(request) {
    const legs = Array.isArray(request?.funding_legs) ? request.funding_legs : [];
    return legs
        .filter((l) => l.kind === 'card' && l.timing === 'captured')
        .reduce((s, l) => s + Number(l.amount || 0), 0);
}

// Wallet cash set aside for a pending request — never actually charged (that
// happens on approval), so withdrawing just releases it back to available.
function walletReservedTotal(request) {
    const legs = Array.isArray(request?.funding_legs) ? request.funding_legs : [];
    return legs
        .filter((l) => l.kind === 'wallet')
        .reduce((s, l) => s + Number(l.amount || 0), 0);
}

/**
 * @param request    the pending request row (with funding_legs)
 * @param onConfirm  async (request) => withdrawResponse; must resolve to the
 *                   server body (for released_to_wallet) or throw an Error whose
 *                   message is client-safe. The modal owns busy + result state.
 * @param onClose    close the modal (also the Done handler).
 */
export default function WithdrawRequestModal({ request, onConfirm, onClose }) {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(null); // { released }

    const noun = REQUEST_NOUN[request?.request_type] || 'request';
    const captured = useMemo(() => capturedCardTotal(request), [request]);
    const reserved = useMemo(() => walletReservedTotal(request), [request]);

    const run = async () => {
        if (submitting) return;
        setSubmitting(true);
        setError('');
        try {
            const res = await onConfirm(request);
            const released = Number(res?.released_to_wallet);
            setDone({ released: Number.isFinite(released) ? Math.max(0, released) : 0 });
        } catch (err) {
            setError(err?.message || 'Could not withdraw this request. Please try again.');
            setSubmitting(false);
        }
    };

    const overlay = {
        // Rendered through a portal to <body> (below) so `position: fixed`
        // measures against the viewport, not a transformed ancestor — otherwise
        // the dim only covers the content column and the sidebar stays bright.
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100000, padding: 16,
    };
    const modal = {
        width: '100%', maxWidth: 440, background: '#fff', borderRadius: 16,
        padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.28)', boxSizing: 'border-box',
    };
    const title = { margin: 0, fontSize: 20, fontWeight: 800, color: '#111827', letterSpacing: '-0.01em' };
    const sub = { margin: '8px 0 0', fontSize: 14, color: '#6b7280', lineHeight: 1.5 };
    const btnRow = { display: 'flex', gap: 10, marginTop: 22 };
    const btnKeep = {
        flex: 1, padding: '11px 16px', borderRadius: 10, border: '1px solid #d1d5db',
        background: '#fff', color: '#374151', fontWeight: 700, fontSize: 14, cursor: 'pointer',
    };
    const btnWithdraw = {
        flex: 1, padding: '11px 16px', borderRadius: 10, border: '1px solid #dc2626',
        background: submitting ? '#ef4444' : '#dc2626', color: '#fff', fontWeight: 700,
        fontSize: 14, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.85 : 1,
    };
    const btnDone = {
        width: '100%', padding: '11px 16px', borderRadius: 10, border: '1px solid #ff5a60',
        background: '#ff5a60', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 22,
    };

    // ---- Done: show the ACTUAL server figure, never the preview ----
    if (done) {
        return createPortal((
            <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
                <div style={modal}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <span style={{
                            width: 34, height: 34, borderRadius: 17, background: '#ecfdf5',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            color: '#059669', fontSize: 18, fontWeight: 900,
                        }}>✓</span>
                        <h2 style={title}>Request withdrawn</h2>
                    </div>
                    <p style={sub}>
                        {done.released > 0.005
                            ? <>Your {noun} was withdrawn and <strong style={{ color: '#059669' }}>{money(done.released)}</strong> from your payment is now in your wallet — you can use it any time, including for a new booking.</>
                            : reserved > 0.005
                                ? <>Your {noun} was withdrawn. The {money(reserved)} that was set aside for it is available in your wallet again.</>
                                : <>Your {noun} was withdrawn. Nothing was charged.</>}
                    </p>
                    <button type="button" style={btnDone} onClick={onClose}>Done</button>
                </div>
            </div>
        ), document.body);
    }

    // ---- Confirm: preview what happens to the money ----
    const refundBox = captured > 0.005
        ? {
            bg: '#ecfdf5', border: '#a7f3d0', fg: '#065f46',
            icon: '↩', text: <>{money(captured)} paid by card will be <strong>returned to your wallet</strong>. You can use it any time — including for a new booking.</>,
        }
        : reserved > 0.005
            ? {
                bg: '#f9fafb', border: '#e5e7eb', fg: '#374151',
                icon: '↺', text: <>The <strong>{money(reserved)}</strong> set aside for this request will be released back to your available wallet balance. Nothing was charged.</>,
            }
            : {
                bg: '#f9fafb', border: '#e5e7eb', fg: '#374151',
                icon: 'ℹ', text: <>Nothing has been charged for this request, so there is nothing to refund.</>,
            };

    return createPortal((
        <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
            <div style={modal}>
                <h2 style={title}>Withdraw this {noun}?</h2>
                <p style={sub}>It will be removed from review and won’t go ahead. You can always submit a new one.</p>

                <div style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16,
                    background: refundBox.bg, border: `1px solid ${refundBox.border}`,
                    borderRadius: 12, padding: '13px 14px',
                }}>
                    <span style={{ fontSize: 16, lineHeight: '20px', color: refundBox.fg }}>{refundBox.icon}</span>
                    <div style={{ fontSize: 13.5, color: refundBox.fg, lineHeight: 1.5 }}>{refundBox.text}</div>
                </div>

                {error && (
                    <div style={{
                        marginTop: 14, background: '#fef2f2', border: '1px solid #fecaca',
                        color: '#b91c1c', borderRadius: 10, padding: '10px 12px', fontSize: 13, lineHeight: 1.5,
                    }}>{error}</div>
                )}

                <div style={btnRow}>
                    <button type="button" style={btnKeep} onClick={onClose} disabled={submitting}>
                        Keep request
                    </button>
                    <button type="button" style={btnWithdraw} onClick={run} disabled={submitting}>
                        {submitting ? 'Withdrawing…' : 'Withdraw request'}
                    </button>
                </div>
            </div>
        </div>
    ), document.body);
}
